import { FetchJsonResponse } from "./types/fetch-json-response";
import HTTP_CODES_ENUM from "./types/http-codes";
import { isSuccessStatus } from "./is-success-status";

// RFC 6838 + RFC 7230 §3.2 compliant JSON content-type detection:
//   • matches `application/json` and any structured-syntax suffix (`+json`)
//     such as `application/problem+json` (RFC 7807) or `application/vnd.api+json`
//   • case-insensitive (HTTP media types are case-insensitive)
//   • tolerates parameters like `; charset=utf-8`
export function isJsonResponse(response: Response): boolean {
    const rawContentType = response.headers.get("content-type") ?? "";
    const mediaType = (rawContentType.split(";")[0] ?? "").trim().toLowerCase();
    return mediaType === "application/json" || mediaType.endsWith("+json");
}

// Defense-in-depth for the Vercel DDoS challenge (Linear #2568):
// the upstream may answer `200 OK` with an HTML challenge body. Remap that
// to SERVICE_UNAVAILABLE so callers branch into their error path instead of
// silently treating an empty response as success (which previously caused
// cart loss). The fast path retry lives in `useFetchBase`.
//
// Total contract (PR #1863 follow-up): `useFetchBase` swallows unrecoverable
// network failures (timeout via `AbortSignal.timeout`, offline, CORS-blocked
// interstitial on an unsafe method) and resolves to `undefined`. Accepting
// `Response | undefined` here makes this the single chokepoint that always
// produces a valid `FetchJsonResponse` for ALL ~23 service callers. Without
// this guard, `undefined.status` threw `TypeError: undefined is not an object`
// that masked the real cause (timeout/offline) in monitoring and reproduced
// reliably on slow FB/IG in-app webviews.
//
// The 503 keeps every `status !== OK` branch working; `transportFailure` tells
// telemetry that no server sent it (see `getAnsweredStatus`).
async function wrapperFetchJsonResponse<T>(
    response: Response | undefined
): Promise<FetchJsonResponse<T>> {
    if (!response) {
        return {
            status: HTTP_CODES_ENUM.SERVICE_UNAVAILABLE,
            data: undefined,
            transportFailure: true,
        };
    }

    const isUnexpectedNonJsonSuccess =
        isSuccessStatus(response.status) &&
        response.status !== HTTP_CODES_ENUM.NO_CONTENT &&
        !isJsonResponse(response);

    if (isUnexpectedNonJsonSuccess) {
        return {
            status: HTTP_CODES_ENUM.SERVICE_UNAVAILABLE,
            data: undefined,
            answeredStatus: response.status,
        };
    }

    const status = response.status as FetchJsonResponse<T>["status"];

    const shouldSkipBody = [
        HTTP_CODES_ENUM.NO_CONTENT,
        HTTP_CODES_ENUM.SERVICE_UNAVAILABLE,
        HTTP_CODES_ENUM.INTERNAL_SERVER_ERROR,
    ].includes(status);

    return {
        status,
        data: shouldSkipBody ? undefined : await response.json(),
    };
}

/**
 * The status the server actually answered with, or `undefined` when the
 * request never got an HTTP response. For failure reports: a failure filed
 * under its stand-in 503 points at a backend that never sent one.
 */
export function getAnsweredStatus<T>(
    response: FetchJsonResponse<T>
): number | undefined {
    if ("transportFailure" in response && response.transportFailure) {
        return undefined;
    }

    return "answeredStatus" in response && response.answeredStatus !== undefined
        ? response.answeredStatus
        : response.status;
}

/**
 * Whether an edge page (the Vercel challenge the remap above exists for)
 * answered in place of our API, so the failure is not our backend's.
 */
export function isEdgeInterception<T>(response: FetchJsonResponse<T>): boolean {
    return "answeredStatus" in response && response.answeredStatus !== undefined;
}

export default wrapperFetchJsonResponse;
